<template>
<div class="home-page">
    <div class="page-shell">

    <!-- Section 01: Hero -->
    <section class="hero">
        <div class="hero-text">
            <div class="hero-tag">// Section 01 / Transmission</div>
            <h1>
                <span class="ln1">Their reviews.</span>
                <span class="ln2">Your roadmap.</span>
                <span class="ln3">No guessing.</span>
            </h1>
            <p class="hero-sub">
                Not sure what to build — or why your app isn't growing? Read your <strong>reviews</strong> and
                your competitors': every <strong>pain</strong>, <strong>feature request</strong> and
                <strong>rank shift</strong> on the App Store and Google Play, turned into a
                <strong>build-ready brief</strong> by your AI agent — or streamed raw by REST.
                Pay per request. <strong>No contracts, no rate-limit theatre.</strong>
            </p>

            <div class="terminal-cmd">
                <span class="prompt">&gt;</span>
                <span class="cmd">/startapp home budget app that actually syncs</span>
                <button class="copy" @click="copyCmd">{{ cmdCopied ? 'Copied' : 'Copy' }}</button>
            </div>

            <div class="hero-cta">
                <router-link to="/startapp" class="btn-amber btn-large">✨ Explore STARTAPP</router-link>
                <router-link v-if="!$state.context.id" to="/registration" class="btn-ghost btn-large">▶ Claim 100 requests</router-link>
                <router-link v-else to="/dashboard" class="btn-ghost btn-large">▶ Open console</router-link>
            </div>
            <div class="hero-meta">
                <b>·</b> 100 free requests &middot; no card &middot; auth in 30 seconds &middot; operating since 2024
            </div>
        </div>

        <div class="radar-box">
            <div class="radar-frame" aria-hidden="true">
                <div class="corner-tl"></div><div class="corner-tr"></div>
                <div class="corner-bl"></div><div class="corner-br"></div>

                <div class="radar-label tl"><span class="v">RADAR</span> · ASR-001</div>
                <div class="radar-label tr">RNG <span class="v">450</span>KM</div>
                <div class="radar-label bl">FRQ <span class="v">2.1</span>GHZ</div>
                <div class="radar-label br">BLIPS <span class="v">{{ blipCount }}</span></div>

                <div class="radar-disc">
                    <div class="radar-rings">
                        <div class="ring r1"></div>
                        <div class="ring r2"></div>
                        <div class="ring r3"></div>
                        <div class="crosshair h"></div>
                        <div class="crosshair v"></div>
                    </div>
                    <div class="radar-sweep"></div>
                    <div class="radar-sweep-arm"></div>

                    <span v-for="(b, i) in blips" :key="i" class="radar-blip" :class="{cool: b.cool}" :style="{top: b.top, left: b.left, animationDelay: b.delay}"></span>

                    <div class="radar-cardinal n">N · 000</div>
                    <div class="radar-cardinal e">090</div>
                    <div class="radar-cardinal s">S · 180</div>
                    <div class="radar-cardinal w">270</div>

                    <div class="radar-center"></div>
                </div>
            </div>
            <div class="radar-readouts">
                <div class="readout">
                    <div class="lbl">Latency p50</div>
                    <div class="val">{{ readoutLatency }}<span class="unit">ms</span></div>
                </div>
                <div class="readout">
                    <div class="lbl">Reviews / day</div>
                    <div class="val">{{ readoutReviews }}<span class="unit">M</span></div>
                </div>
                <div class="readout">
                    <div class="lbl">Stores online</div>
                    <div class="val">{{ readoutStores }}<span class="unit">/ 2</span></div>
                </div>
                <div class="readout">
                    <div class="lbl">Uptime &middot; 30d</div>
                    <div class="val">{{ readoutUptime }}<span class="unit">%</span></div>
                </div>
            </div>
        </div>
    </section>

    <!-- Section 02: STARTAPP -->
    <header class="section-head">
        <div class="section-tag">// Section 02 / STARTAPP</div>
        <h2 class="section-title">Their pain. Your product.</h2>
        <div class="section-meta">prompt &rarr; pain map &rarr; build brief</div>
    </header>
    <div class="zti-grid home-zti">
        <div class="zti-step">
            <div class="zti-n">01</div>
            <div class="zti-mock" aria-hidden="true">
                <div class="zti-mock-bar">claude.ai · prompt</div>
                <div class="zti-mock-line">/startapp <b>fitness tracker for climbers</b></div>
                <div class="zti-mock-line dim">agent shortlists competitors on both stores…</div>
            </div>
            <h3>Describe the app</h3>
            <p>One sentence. Your agent finds the niche, the incumbents, and their review firehose.</p>
        </div>
        <div class="zti-step">
            <div class="zti-n">02</div>
            <div class="zti-mock" aria-hidden="true">
                <div class="zti-mock-bar">gap matrix</div>
                <div class="zti-mock-line">ads &middot; 2 apps &middot; 4,163 complaints</div>
                <div class="zti-mock-line dim">sync issues &middot; crashes &middot; pricing &middot; +261 more</div>
            </div>
            <h3>Mine the pain</h3>
            <p>Complaint topics ranked across the whole niche — what everyone's users beg for and nobody ships.</p>
        </div>
        <div class="zti-step">
            <div class="zti-n">03</div>
            <div class="zti-mock" aria-hidden="true">
                <div class="zti-mock-bar">startapp brief</div>
                <div class="zti-mock-chip">✓ 84 market gaps</div>
                <div class="zti-mock-chip">✓ opportunity 100</div>
                <div class="zti-mock-line dim">ASO keys &middot; PRD narrative &middot; then the agent writes code</div>
            </div>
            <h3>Get the brief</h3>
            <p>A build-ready blueprint — and your agent keeps going, straight into the code.</p>
        </div>
    </div>
    <div class="home-section-cta">
        <router-link to="/startapp" class="btn-amber btn-large">✨ STARTAPP</router-link>
    </div>

    <!-- Section 03: Hosted MCP -->
    <header class="section-head">
        <div class="section-tag">// Section 03 / MCP</div>
        <h2 class="section-title">Plug your AI in</h2>
        <div class="section-meta">hosted &middot; one URL &middot; nothing to install</div>
    </header>
    <div class="home-mcp">
        <div class="terminal-cmd">
            <span class="prompt">#</span>
            <span class="cmd">https://mcp.asoradar.com/mcp</span>
        </div>
        <div class="hero-meta">
            <b>·</b> 46 tools across both stores &middot; connecting &amp; listing tools is free &middot;
            /startapp command ships with the connector &middot; OAuth or access key
        </div>
        <div class="home-section-cta">
            <router-link to="/mcp" class="btn-ghost btn-large">// Connect Claude, Cursor, anything MCP</router-link>
        </div>
    </div>

    <!-- Section 04: Transmission protocol (code) -->
    <header class="section-head">
        <div class="section-tag">// Section 04 / API + SDK</div>
        <h2 class="section-title">API + SDK</h2>
        <div class="section-meta">12 languages supported</div>
    </header>

    <CodeExamples />
    <div class="lang-extra">
        // 12 languages &middot; <router-link to="/docs">read the manual</router-link> for the full list
    </div>

    <!-- Section 02: Capability matrix -->
    <header class="section-head">
        <div class="section-tag">// Section 05</div>
        <h2 class="section-title">Capability matrix</h2>
        <div class="section-meta">06 modules &middot; all operational</div>
    </header>

    <div class="caps-grid">
        <div v-for="(cap, i) in caps" :key="cap.callsign" :class="`cap cap-${i + 1}`">
            <div class="cap-head">
                <span class="cap-callsign">{{ cap.callsign }}</span>
                <span class="cap-status" :class="{cool: cap.cool}"><span class="dot"></span>{{ cap.status }}</span>
            </div>
            <div class="cap-title">{{ cap.title }}</div>
            <div class="cap-body" v-html="cap.body"></div>
            <div class="coverage-bar" v-if="cap.coverage"><div class="fill" :style="{width: cap.coverage}"></div></div>
            <div class="cap-foot">
                <span v-for="(f, j) in cap.foot" :key="j" v-html="f"></span>
            </div>
        </div>
    </div>

    <!-- Section 03: Tier registry -->
    <header class="section-head">
        <div class="section-tag">// Section 06</div>
        <h2 class="section-title">Tier registry</h2>
        <div class="section-meta">04 frequencies &middot; select one</div>
    </header>

    <div class="pricing-grid">
        <div v-for="tier in tiers" :key="tier.frq" class="tier" :class="{featured: tier.featured}">
            <div class="tier-tag">
                <span>{{ tier.frq }}</span>
                <span :style="tier.featured ? {color: 'var(--accent)'} : {}">{{ tier.code }}</span>
            </div>
            <div class="tier-name">{{ tier.name }}</div>
            <div class="tier-price"><span class="currency">$</span>{{ tier.price }}</div>
            <div class="tier-unit">{{ tier.unit }}</div>
            <ul class="tier-feats">
                <li v-for="(f, i) in tier.features" :key="i" :class="{x: f.startsWith('-')}">{{ f.replace(/^-\s*/, '') }}</li>
            </ul>
            <router-link :to="tier.cta_to" class="tier-cta">{{ tier.cta }}</router-link>
        </div>
    </div>

    <!-- Section 05: Initiate -->
    <section class="initiate">
        <div>
            <h2>Start <em>scanning.</em><br>Stop <em>guessing.</em></h2>
            <p>
                100 free requests on your first key. No card, no contract, no demo call. Issue your first credential and place your first call before this page finishes loading.
            </p>
        </div>
        <div class="right">
            <router-link v-if="!$state.context.id" to="/registration" class="btn-amber btn-large" style="font-size:14px; padding:18px 32px;">▶ Register station</router-link>
            <router-link v-else to="/dashboard" class="btn-amber btn-large" style="font-size:14px; padding:18px 32px;">▶ Open console</router-link>
            <span class="or">// or &middot; <router-link to="/docs">read the manual first</router-link></span>
        </div>
    </section>

    </div>
</div>
</template>

<script>
import CodeExamples from './CodeExamples'

const RAW_CMD = 'curl -G https://api.asoradar.com/a1/apps/389801252/reviews -d access_key=YOUR_KEY'

export default {
    components: { CodeExamples },
    data() {
        return {
            cmdCopied: false,
            blipCount: 218,
            readoutLatency: 0,
            readoutReviews: '0.00',
            readoutStores: 0,
            readoutUptime: '0.00',
            blips: [
                { top: '18%', left: '64%', delay: '0.4s' },
                { top: '28%', left: '78%', delay: '0.9s' },
                { top: '52%', left: '82%', delay: '1.4s', cool: true },
                { top: '72%', left: '70%', delay: '2.1s' },
                { top: '80%', left: '50%', delay: '2.6s' },
                { top: '76%', left: '32%', delay: '3.0s', cool: true },
                { top: '58%', left: '18%', delay: '3.4s' },
                { top: '38%', left: '22%', delay: '3.8s' },
                { top: '24%', left: '38%', delay: '0.2s', cool: true },
                { top: '48%', left: '60%', delay: '1.7s' },
            ],
            caps: [
                {
                    callsign: 'CAP-01 · DUAL-STORE', status: 'OPERATIONAL',
                    title: 'App Store + Google Play. One protocol.',
                    body: 'A single REST surface for both stores. Identical request shapes. Identical response schemas. The store ID lives in the URL — your code does not.',
                    coverage: '100%',
                    foot: ['iOS · <b>App Store</b>', 'Android · <b>Google Play</b>', 'Coverage <b>100%</b>'],
                },
                {
                    callsign: 'CAP-02 · LIVE INGEST', status: 'STREAMING', cool: true,
                    title: 'Real-time scraping. On demand.',
                    body: 'Trigger a fresh scan and receive results in seconds. No scheduled lag. No stale snapshots pretending to be real.',
                    foot: ['Trigger <b>POST</b>', 'Latency <b>p50 132ms</b>'],
                },
                {
                    callsign: 'CAP-03 · FILTERS', status: 'OPERATIONAL',
                    title: 'Slice it any way.',
                    body: 'Rating, date, language, country, version, full-text. Stack the filters server-side, get back exactly what you need.',
                    foot: ['9 dims', 'FTS <b>on</b>'],
                },
                {
                    callsign: 'CAP-04 · BILLING', status: 'OPERATIONAL',
                    title: 'Pay per request. Period.',
                    body: 'No subscriptions, no minimum commits, no surprise overage tier. From <b>$0.0005</b> per call. Stop calling, stop paying.',
                    foot: ['Min <b>$0.0005</b>', 'No floor'],
                },
                {
                    callsign: 'CAP-05 · AUTH', status: 'OPERATIONAL',
                    title: 'One key. Everything.',
                    body: 'A single access key opens every endpoint. No OAuth dance, no JWT refresh choreography. Issue, rotate, revoke from the console.',
                    foot: ['<b>1 key</b> · all endpoints', 'Rotate <b>any time</b>'],
                },
                {
                    callsign: 'CAP-06 · TELEMETRY', status: 'BROADCASTING', cool: true,
                    title: 'Console with the lights on.',
                    body: 'Every request, every error, every cent. Live usage charts, token-level rate limits, payment ledger, downloadable invoices.',
                    foot: ['Realtime <b>charts</b>', 'Per-token <b>limits</b>', 'CSV <b>export</b>'],
                },
            ],
            tiers: [
                {
                    frq: 'FRQ-01', code: 'TRIAL', name: 'Trial', price: '0',
                    unit: 'Forever free · 100 calls',
                    features: ['100 free requests', 'Both stores', 'All filters', '- No support'],
                    cta: 'Start free', cta_to: '/registration',
                },
                {
                    frq: 'FRQ-02', code: 'STARTER', name: 'Starter', price: '0.002',
                    unit: 'per request · 10k included',
                    features: ['10,000 requests', 'Both stores', 'Standard scraping', 'Email support'],
                    cta: 'Get started', cta_to: '/registration',
                },
                {
                    frq: 'FRQ-03', code: 'DEVELOPER', name: 'Developer', price: '0.001',
                    featured: true,
                    unit: 'per request · unlimited',
                    features: ['Unlimited requests', 'Priority scraping', 'Unlimited tokens', 'Per-token rate limits'],
                    cta: '▶ Deploy now', cta_to: '/registration',
                },
                {
                    frq: 'FRQ-04', code: 'ENTERPRISE', name: 'Enterprise', price: '0.0005',
                    unit: 'per request · volume',
                    features: ['Volume pricing', 'Dedicated support', 'Custom SLA', 'Invoice billing'],
                    cta: 'Talk to ops', cta_to: '/registration',
                },
            ],
        }
    },
    mounted() {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            this.readoutLatency = 132
            this.readoutReviews = '4.70'
            this.readoutStores = 2
            this.readoutUptime = '99.97'
            return
        }
        this._easeTo({ key: 'readoutLatency', target: 132, decimals: 0, ms: 1400 })
        this._easeTo({ key: 'readoutReviews', target: 4.7, decimals: 2, ms: 1400 })
        this._easeTo({ key: 'readoutStores', target: 2, decimals: 0, ms: 800 })
        this._easeTo({ key: 'readoutUptime', target: 99.97, decimals: 2, ms: 1400 })
    },
    methods: {
        _easeTo({ key, target, decimals, ms }) {
            const start = performance.now()
            const step = (now) => {
                const t = Math.min(1, (now - start) / ms)
                const eased = 1 - Math.pow(1 - t, 3)
                const v = target * eased
                this[key] = decimals ? v.toFixed(decimals) : Math.round(v)
                if (t < 1) requestAnimationFrame(step)
                else this[key] = decimals ? target.toFixed(decimals) : target
            }
            requestAnimationFrame(step)
        },
        copyCmd() {
            navigator.clipboard.writeText(RAW_CMD).then(() => {
                this.cmdCopied = true
                setTimeout(() => { this.cmdCopied = false }, 1600)
            })
        },
    },
}
</script>

<style scoped>
.home-page :deep(.page-shell) { padding: 0 40px; }
@media (max-width: 640px) { .home-page :deep(.page-shell) { padding: 0 20px; } }

.home-zti { padding: 0 40px; }
@media (max-width: 640px) { .home-zti { padding: 0 20px; } }
.home-section-cta { text-align: center; margin: 28px 0 56px; }
.home-mcp { max-width: 760px; margin: 0 auto 56px; padding: 0 20px; text-align: center; }
.home-mcp .terminal-cmd { justify-content: center; }
</style>
